export type GanttCategory = {
  id: string
  label: string
  backgroundColor?: string
}

export type GanttCaption = {
  id: string
  label: string
  color?: string
}

export const ganttDefaultCaptions: GanttCaption[] = [
  { id: 'starting', label: 'Starting', color: '#DDE5ED' },
  { id: 'in-progress', label: 'In Progress', color: '#FFDE69' },
  { id: 'finished', label: 'Finished', color: '#74EC81' }
]

export const buildCaptionColorMap = (
  captions: GanttCaption[]
): Record<string, string | undefined> =>
  Object.fromEntries(captions.map((caption) => [caption.id, caption.color]))

export const resolveGanttCardBackgroundColor = (
  card: Pick<GanttCard, 'color' | 'captionId'>,
  captionColorById: Record<string, string | undefined>
): string =>
  card.color ??
  (card.captionId ? captionColorById[card.captionId] : undefined) ??
  '#dbeafe'

export type GanttCard = {
  id: string
  categoryId: string
  rowIndex: number
  start: string
  end: string
  label: string
  color?: string
  captionId?: string
}

export type CardLayout = {
  left: number
  width: number
  top: number
  lane: number
  globalRowIndex: number
}

export type RowLayout = {
  top: number
  height: number
  globalRowIndex: number
  categoryId: string
  rowIndex: number
}

export type CategoryLayout = {
  top: number
  height: number
  startRowIndex: number
}

export type GanttRowDescriptor = {
  globalRowIndex: number
  categoryId: string
  rowIndex: number
}

export type GanttLayout = {
  cardLayoutMap: Record<string, CardLayout>
  rowLayoutMap: Record<string, RowLayout>
  categoryLayoutMap: Record<string, CategoryLayout>
  rowList: GanttRowDescriptor[]
  totalBodyHeight: number
}

const DAY_MS = 24 * 60 * 60 * 1000
export const MONTH_COLUMNS = 12
export const DEFAULT_ROW_HEIGHT = 96
export const DEFAULT_CARD_HEIGHT = 44
export const DEFAULT_CARD_LANE_GAP = 8
export const DEFAULT_MONTH_COLUMN_GAP = 4

const toUtcDate = (iso: string) => {
  const parsed = new Date(iso)
  return new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
  )
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value))

const getMonthIndexFromTimestamp = (timestamp: number) =>
  new Date(timestamp).getUTCMonth()

export const getYearBounds = (year: number) => {
  const yearStart = Date.UTC(year, 0, 1)
  const yearEnd = Date.UTC(year + 1, 0, 1) - DAY_MS
  return { yearStart, yearEnd }
}

const MONTH_NAME_REFERENCE_YEAR = 2000

export const getDefaultMonths = (locale = 'pt-BR') =>
  Array.from({ length: MONTH_COLUMNS }, (_, monthIndex) =>
    new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
      new Date(Date.UTC(MONTH_NAME_REFERENCE_YEAR, monthIndex, 1))
    )
  ).map((month) => month.charAt(0).toUpperCase() + month.slice(1))

export const getMonthDayFractions = (year: number) => {
  const daysPerMonth = Array.from({ length: MONTH_COLUMNS }, (_, monthIndex) =>
    new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
  )
  const totalDays = daysPerMonth.reduce((sum, days) => sum + days, 0)
  return daysPerMonth.map((days) => days / totalDays)
}

export const getMonthColumnHighlightBounds = ({
  monthIndex,
  timelineWidth,
  monthColumnGap,
  year
}: {
  monthIndex: number
  timelineWidth: number
  monthColumnGap: number
  year: number
}): { left: number; width: number } | null => {
  if (monthIndex < 0 || monthIndex >= MONTH_COLUMNS) return null
  const fractions = getMonthDayFractions(year)
  const totalMonthGapWidth = Math.max(0, MONTH_COLUMNS - 1) * monthColumnGap
  const timelineTrackWidth = Math.max(0, timelineWidth - totalMonthGapWidth)
  const widths = fractions.map((f) => f * timelineTrackWidth)
  let left = 0
  for (let i = 0; i < monthIndex; i += 1) {
    left += widths[i] + monthColumnGap
  }
  return { left, width: widths[monthIndex] }
}

const getTimelineScaleWidth = (
  timelineWidth: number,
  monthColumnGap: number
) => {
  const totalMonthGapWidth = Math.max(0, MONTH_COLUMNS - 1) * monthColumnGap
  return Math.max(0, timelineWidth - totalMonthGapWidth)
}

type DateToTimelinePositionArgs = {
  isoDate: string
  year: number
  timelineWidth: number
  monthColumnGap?: number
  includeDay?: boolean
}

export const getDatePositionOnTimeline = ({
  isoDate,
  year,
  timelineWidth,
  monthColumnGap = DEFAULT_MONTH_COLUMN_GAP,
  includeDay = false
}: DateToTimelinePositionArgs) => {
  if (!isoDate) return null

  const parsedDate = new Date(isoDate)
  if (Number.isNaN(parsedDate.getTime())) return null

  const { yearStart, yearEnd } = getYearBounds(year)
  const yearEndExclusive = yearEnd + DAY_MS
  const yearDuration = yearEndExclusive - yearStart
  const timelineScaleWidth = getTimelineScaleWidth(
    timelineWidth,
    monthColumnGap
  )
  const utcDate = toUtcDate(isoDate).getTime()
  const rawDate = includeDay ? utcDate + DAY_MS : utcDate
  const clampedDate = clamp(rawDate, yearStart, yearEndExclusive)
  const dateProgress = (clampedDate - yearStart) / yearDuration
  const monthGapOffset = getMonthIndexFromTimestamp(utcDate) * monthColumnGap

  return dateProgress * timelineScaleWidth + monthGapOffset
}

const normalizeCardRange = (card: GanttCard, year: number) => {
  const { yearStart, yearEnd } = getYearBounds(year)
  const start = clamp(toUtcDate(card.start).getTime(), yearStart, yearEnd)
  const end = clamp(toUtcDate(card.end).getTime(), yearStart, yearEnd)
  return start <= end ? { start, end } : { start: end, end: start }
}

export const getRowKey = (categoryId: string, rowIndex: number) =>
  `${categoryId}-${rowIndex}`

const getCategoryRowCount = (cards: GanttCard[], categoryId: string) => {
  const categoryCards = cards.filter((card) => card.categoryId === categoryId)
  const highestRowIndex = categoryCards.reduce(
    (highestRow, card) => Math.max(highestRow, card.rowIndex),
    -1
  )

  return Math.max(1, highestRowIndex + 1)
}

export const getCardLayoutMap = ({
  cards,
  categories,
  year,
  timelineWidth,
  rowHeight = DEFAULT_ROW_HEIGHT,
  cardHeight = DEFAULT_CARD_HEIGHT,
  laneGap = DEFAULT_CARD_LANE_GAP,
  monthColumnGap = DEFAULT_MONTH_COLUMN_GAP
}: {
  cards: GanttCard[]
  categories: GanttCategory[]
  year: number
  timelineWidth: number
  rowHeight?: number
  cardHeight?: number
  laneGap?: number
  monthColumnGap?: number
}): GanttLayout => {
  const cardLayoutMap: Record<string, CardLayout> = {}
  const rowLayoutMap: Record<string, RowLayout> = {}
  const categoryLayoutMap: Record<string, CategoryLayout> = {}
  const rowList: GanttRowDescriptor[] = []
  let currentTop = 0
  let globalRowIndex = 0

  categories.forEach((category) => {
    const categoryStartTop = currentTop
    const categoryStartRowIndex = globalRowIndex
    const categoryRowCount = getCategoryRowCount(cards, category.id)

    for (let rowIndex = 0; rowIndex < categoryRowCount; rowIndex++) {
      const rowKey = getRowKey(category.id, rowIndex)
      const rowTop = currentTop

      rowLayoutMap[rowKey] = {
        top: rowTop,
        height: rowHeight,
        globalRowIndex,
        categoryId: category.id,
        rowIndex
      }

      rowList.push({
        globalRowIndex,
        categoryId: category.id,
        rowIndex
      })

      const rowCards = cards
        .filter(
          (card) =>
            card.categoryId === category.id && card.rowIndex === rowIndex
        )
        .map((card) => {
          const range = normalizeCardRange(card, year)
          return { ...card, rangeStart: range.start, rangeEnd: range.end }
        })
        .sort((a, b) => a.rangeStart - b.rangeStart)

      const laneCount = rowCards.length
      const stackedHeight =
        laneCount > 0 ? laneCount * cardHeight + (laneCount - 1) * laneGap : 0
      const verticalPadding = Math.max(0, (rowHeight - stackedHeight) / 2)

      rowCards.forEach((card, lane) => {
        const left =
          getDatePositionOnTimeline({
            isoDate: card.start,
            year,
            timelineWidth,
            monthColumnGap
          }) ?? 0
        const right =
          getDatePositionOnTimeline({
            isoDate: card.end,
            year,
            timelineWidth,
            monthColumnGap,
            includeDay: true
          }) ?? left
        const width = Math.max(8, right - left)
        const top = rowTop + verticalPadding + lane * (cardHeight + laneGap)

        cardLayoutMap[card.id] = {
          left,
          width,
          top,
          lane,
          globalRowIndex
        }
      })

      currentTop += rowHeight
      globalRowIndex += 1
    }

    categoryLayoutMap[category.id] = {
      top: categoryStartTop,
      height: rowHeight * categoryRowCount,
      startRowIndex: categoryStartRowIndex
    }
  })

  return {
    cardLayoutMap,
    rowLayoutMap,
    categoryLayoutMap,
    rowList,
    totalBodyHeight: currentTop
  }
}

export type GanttFooterMilestone = {
  label: string
  date: string
  description?: string
}

const INVALID_DATE_SORT_VALUE = Number.MAX_SAFE_INTEGER

export const sortGanttFooterMilestones = (
  milestones: GanttFooterMilestone[] | undefined | null
): GanttFooterMilestone[] => {
  return [...(milestones ?? [])]
    .sort((first, second) => {
      const firstTime = new Date(first.date).getTime()
      const secondTime = new Date(second.date).getTime()
      const firstSort = Number.isNaN(firstTime)
        ? INVALID_DATE_SORT_VALUE
        : firstTime
      const secondSort = Number.isNaN(secondTime)
        ? INVALID_DATE_SORT_VALUE
        : secondTime
      return firstSort - secondSort
    })
    .filter((milestone) => !Number.isNaN(new Date(milestone.date).getTime()))
}

export const isCurrentYear = (year: number) => new Date().getFullYear() === year
